import React, { useState, useId, useMemo } from "react";
import { Link } from "react-router-dom";
import { convertContentfulNotesFromString } from "helpers/dataConversion/convertContentfulNotesFromString";
import {
  getEventDetailLink,
  isInternalEventLink,
} from "helpers/events/getEventDetailLink";
import { AnalyticsEvents, trackEvent } from "helpers/analytics/analytics";
import useTrackWhenVisible from "hooks/useTrackWhenVisible";
import "./EventCard.css";

const EventCard = ({ event }) => {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const fields = event.fields;
  const detailLink = getEventDetailLink(fields);
  const hasDedicatedPage = Boolean(detailLink);

  const viewParams = useMemo(
    () => ({
      item_name: fields.name,
      location: "events_page",
    }),
    [fields.name]
  );

  const viewRef = useTrackWhenVisible(AnalyticsEvents.EVENT_VIEW, viewParams);

  const notes = useMemo(() => {
    const parsed = convertContentfulNotesFromString(fields.notes);
    return parsed || [];
  }, [fields.notes]);

  const hasInlineDetails =
    !hasDedicatedPage &&
    (fields.date ||
      fields.time ||
      fields.price ||
      fields.spectatorPrice ||
      fields.notes);

  const imageUrl = fields.media?.fields?.file?.url;

  const trackDetailNavigation = () => {
    trackEvent(AnalyticsEvents.EVENT_DETAILS, {
      item_name: fields.name,
      location: "events_page",
      label: "dedicated_page",
    });
  };

  const detailButtonClassName = "event-card__toggle event-card__detail-link";

  const detailButton =
    hasDedicatedPage &&
    (isInternalEventLink(detailLink) ? (
      <Link
        to={detailLink}
        className={detailButtonClassName}
        onClick={trackDetailNavigation}
        data-track="event_details_nav_click"
        data-track-location="events_page"
        data-track-item={fields.name}
      >
        View details
      </Link>
    ) : (
      <a
        href={detailLink}
        className={detailButtonClassName}
        onClick={trackDetailNavigation}
        data-track="event_details_nav_click"
        data-track-location="events_page"
        data-track-item={fields.name}
      >
        View details
      </a>
    ));

  return (
    <article className="event-card" ref={viewRef}>
      {imageUrl && (
        <div className="event-card__image-wrap">
          <img
            src={imageUrl}
            alt={`${fields.name} at Ace Gymnastics in Bardstown, KY`}
            width="400"
            height="260"
            loading="lazy"
          />
        </div>
      )}

      <div className="event-card__body">
        <h2 className="event-card__title">{fields.name}</h2>

        {fields.description && (
          <p
            className={`event-card__description ${
              expanded || !hasInlineDetails || hasDedicatedPage ? "is-expanded" : ""
            }`}
          >
            {fields.description}
          </p>
        )}

        {hasInlineDetails && (
          <div
            id={detailsId}
            className={`event-card__details ${expanded ? "is-expanded" : ""}`}
            hidden={!expanded}
          >
            <dl className="event-card__meta">
              {fields.date && (
                <>
                  <dt>Date</dt>
                  <dd>{fields.date}</dd>
                </>
              )}
              {fields.time && (
                <>
                  <dt>Time</dt>
                  <dd>{fields.time}</dd>
                </>
              )}
              {fields.price && (
                <>
                  <dt>Price</dt>
                  <dd>{fields.price}</dd>
                </>
              )}
              {fields.spectatorPrice && (
                <>
                  <dt>Spectator price</dt>
                  <dd>{fields.spectatorPrice}</dd>
                </>
              )}
            </dl>

            {notes.length > 0 && (
              <div className="event-card__notes">
                <p className="event-card__notes-label">Additional info</p>
                <ul>
                  {notes.map((note, index) => (
                    <li key={index}>{note.trim()}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="event-card__actions">
          {detailButton}

          {hasInlineDetails && (
            <button
              type="button"
              className="event-card__toggle"
              aria-expanded={expanded}
              aria-controls={detailsId}
              onClick={() => {
                setExpanded((v) => {
                  const next = !v;
                  trackEvent(AnalyticsEvents.EVENT_DETAILS, {
                    item_name: fields.name,
                    location: "events_page",
                    label: next ? "expand" : "collapse",
                  });
                  return next;
                });
              }}
            >
              {expanded ? "Hide details" : "View details"}
            </button>
          )}

          {fields.registrationLink && (
            <a
              href={fields.registrationLink}
              target="_blank"
              rel="noopener noreferrer"
              className="event-card__register home-btn home-btn--primary"
              data-track="event_register_click"
              data-track-location="events_page"
              data-track-item={fields.name}
            >
              Register
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

export default EventCard;
